// === API Response Types ===

export interface CuitResponse {
  id: number;
  cuit: string;
  razonSocial: string;
  domicilio: string;
  inicioActividades: string;
  puntoVenta: number;
  environment: "homologacion" | "produccion";
  ownerId: string;
  isOwner: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CuitUserResponse {
  id: number;
  cuitId: number;
  userEmail: string;
  userId: string | null;
  createdAt: string;
}

export interface TransferResponse {
  id: number;
  cuitId: number;
  referenceId: string;
  releaseDate: string;
  payerName: string;
  netAmount: number;
  status: "pending" | "processing" | "invoiced" | "failed";
  invoiceId: number | null;
  importedBy: string;
  createdAt: string;
}

export interface InvoiceResponse {
  id: number;
  cuitId: number;
  tipoCbte: number;
  puntoVenta: number;
  numeroCbte: number;
  fechaCbte: string;
  concepto: number;
  description: string | null;
  receptorNombre: string | null;
  impTotal: number;
  cae: string;
  caeVto: string;
  fchServDesde: string | null;
  fchServHasta: string | null;
  associatedTipoCbte?: number | null;
  associatedPuntoVenta?: number | null;
  associatedNumeroCbte?: number | null;
  associatedInvoiceId?: number | null;
  hasCreditNote?: boolean;
  emittedBy: string;
  createdAt: string;
}

// === Request Types ===

export interface CreateCuitRequest {
  cuit: string;
  razonSocial: string;
  domicilio: string;
  inicioActividades: string;
  puntoVenta: number;
  environment: "homologacion" | "produccion";
  // cert and key are sent as base64 in the JSON body
  certBase64: string;
  keyBase64: string;
}

export interface UpdateCuitRequest {
  razonSocial?: string;
  domicilio?: string;
  inicioActividades?: string;
  puntoVenta?: number;
  environment?: "homologacion" | "produccion";
  certBase64?: string;
  keyBase64?: string;
}

export interface InviteUserRequest {
  email: string;
}

export interface ImportTransfersRequest {
  transfers: {
    referenceId: string;
    releaseDate: string;
    payerName: string;
    netAmount: number;
  }[];
}

export interface UpdateTransferRequest {
  payerName?: string;
  netAmount?: number;
  description?: string;
}

export interface EmitInvoiceRequest {
  transferIds: number[];
  idempotencyKey: string; // UUID v4 por batch para evitar duplicados
  concepto: 1 | 2 | 3; // 1=Productos, 2=Servicios, 3=Ambos
  description?: string;
  invoiceDate?: string; // YYYY-MM-DD
}

export interface CreateCreditNoteBatchRangeRequest {
  fromNumber: number;
  toNumber: number;
  associatedPuntoVenta?: number;
  idempotencyKey: string;
  description?: string;
  invoiceDate?: string; // YYYY-MM-DD
}

export interface EmitManualInvoiceRequest {
  idempotencyKey?: string; // opcional, backend lo genera si no se envía
  concepto: 1 | 2 | 3;
  description?: string;
  receptorNombre?: string;
  impTotal: number;
  invoiceDate?: string; // YYYY-MM-DD
}

export interface InvoiceDateConstraintsResponse {
  concepto: 1 | 2 | 3;
  timezone: string;
  businessDate: string;
  minDate: string;
  maxDate: string;
  lastAuthorizedDate: string | null;
  lastAuthorizedNumber: number;
  source: "arca";
}

export interface EmitInvoiceResult {
  transferId: number | null;
  success: boolean;
  invoice?: InvoiceResponse;
  error?: string;
}

export type InvoiceEmissionJobStatus =
  | "queued"
  | "waiting_lock"
  | "processing"
  | "success"
  | "partial"
  | "failed";

export type InvoiceEmissionJobKind = "manual" | "batch" | "credit_note_batch_range";

export interface InvoiceEmissionJobItem {
  transferId: number | null;
  sourceTipoCbte?: number | null;
  sourcePuntoVenta?: number | null;
  sourceNumeroCbte?: number | null;
  stage: string;
  status: "processing" | "success" | "failed";
  arcaInvoiceNumber: number | null;
  errorMessage: string | null;
  invoice: InvoiceResponse | null;
}

export interface InvoiceEmissionJob {
  id: number;
  requestId: string;
  kind: InvoiceEmissionJobKind;
  status: InvoiceEmissionJobStatus;
  totalItems: number;
  processedItems: number;
  successItems: number;
  failedItems: number;
  stopReason: string | null;
  errorMessage: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  items: InvoiceEmissionJobItem[];
}

export interface EmitInvoiceAcceptedResponse {
  jobId: number;
  requestId: string;
  status: InvoiceEmissionJobStatus;
  rangeRequestedCount?: number;
  estimatedArcaCallsNominal?: number;
  estimatedArcaCallsWorstCase?: number;
}

export interface InvoiceJobsSummaryResponse {
  activeCount: number;
  latestUpdateAt: string | null;
}

export interface CreditNoteAttemptResponse {
  id: number;
  sourceTipoCbte: number;
  sourcePuntoVenta: number;
  sourceNumeroCbte: number;
  sourceDocTipo: number | null;
  sourceDocNro: number | null;
  sourceConcepto: number | null;
  sourceFechaCbte: string | null;
  sourceImpTotal: number | null;
  stage: string;
  status: "processing" | "success" | "failed";
  arcaInvoiceNumber: number | null;
  errorMessage: string | null;
  creditNoteInvoiceId: number | null;
}

export type CreditNoteResponse = InvoiceResponse;

// === Pagination ===

export interface PaginationMeta {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination?: PaginationMeta;
  hasMore?: boolean;
  nextCursor?: string;
}

// === CSV Parsing ===

export interface ParsedTransfer {
  referenceId: string;
  releaseDate: string; // ISO 8601
  payerName: string;
  netAmount: number;
}

// === Dashboard ===

export interface DashboardRecentInvoice {
  id: number;
  puntoVenta: number;
  numeroCbte: number;
  fechaCbte: string;
  receptorNombre: string | null;
  impTotal: number;
  cae: string;
}

export interface DashboardComparison {
  invoicedCountDelta: number;
  invoicedCountDeltaPct: number | null;
  totalAmountDelta: number;
  totalAmountDeltaPct: number | null;
  avgTicketDelta: number;
  avgTicketDeltaPct: number | null;
}

export interface DashboardStats {
  month: string;
  previousMonth: string;
  pendingTransfers: number;
  invoicedCount: number;
  totalAmount: number;
  avgTicket: number;
  comparison: DashboardComparison;
  recentInvoices: DashboardRecentInvoice[];
}

// === ARCA Dashboard ===

export interface ArcaServerStatus {
  appServer: string;
  dbServer: string;
  authServer: string;
}

export interface ArcaPtoVenta {
  nro: number;
  emisionTipo: string;
  bloqueado: string;
  fchBaja: string;
}

export interface ArcaParamItem {
  id: number;
  desc: string;
  fchDesde: string;
  fchHasta: string;
}

export interface ArcaDashboardData {
  serverStatus: ArcaServerStatus | null;
  lastInvoiceNumber: number | null;
  maxBatchSize: number | null;
  tokenExpires: string | null;
  environment: string;
  puntoVenta: number;
  pointsOfSale: ArcaPtoVenta[] | null;
  invoiceTypes: ArcaParamItem[] | null;
  conceptTypes: ArcaParamItem[] | null;
  documentTypes: ArcaParamItem[] | null;
}

// === Validators ===

export function isValidCuit(cuit: string): boolean {
  return /^\d{11}$/.test(cuit);
}

export function formatCuitDisplay(cuit: string): string {
  if (cuit.length !== 11) return cuit;
  return `${cuit.slice(0, 2)}-${cuit.slice(2, 10)}-${cuit.slice(10)}`;
}

export function parseArgentineNumber(value: string): number {
  // Argentine format: 169.000,00 → 169000.00
  return parseFloat(value.replace(/\./g, "").replace(",", "."));
}

export function parseArgentineDate(value: string): string {
  // DD-MM-YYYY → YYYY-MM-DD (ISO 8601)
  const [day, month, year] = value.split("-");
  return `${year}-${month}-${day}`;
}

export function parseMercadoPagoCSV(csvContent: string): ParsedTransfer[] {
  const lines = csvContent.split("\n");

  // Find the transaction headers line (contains RELEASE_DATE)
  let dataStartIndex = -1;
  for (let i = 0; i < lines.length; i++) {
    if (lines[i].includes("RELEASE_DATE")) {
      dataStartIndex = i + 1;
      break;
    }
  }

  if (dataStartIndex === -1) return [];

  const results: ParsedTransfer[] = [];

  for (let i = dataStartIndex; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    const fields = line.split(";");
    if (fields.length < 4) continue;

    const [releaseDate, transactionType, referenceId, netAmount] = fields;

    // Only import "Transferencia recibida" rows
    if (!transactionType.startsWith("Transferencia recibida")) continue;

    const payerName = transactionType.replace("Transferencia recibida ", "").trim();

    results.push({
      referenceId: referenceId.trim(),
      releaseDate: parseArgentineDate(releaseDate.trim()),
      payerName,
      netAmount: parseArgentineNumber(netAmount.trim()),
    });
  }

  return results;
}

// === Concepto labels ===

export const CONCEPTO_LABELS: Record<number, string> = {
  1: "Productos",
  2: "Servicios",
  3: "Productos y Servicios",
};
